package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMBodyStrongTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMDropzoneHeightVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMListRowPaddingBlockVar
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onDragLeave
import com.varabyte.kobweb.compose.ui.modifiers.onDragOver
import com.varabyte.kobweb.compose.ui.modifiers.onDrop
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.css.TextAlign
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import org.w3c.files.get

val WMDropzoneStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .minHeight(WMDropzoneHeightVar.value())
            .border(WMHairlineVar.value(), LineStyle.Dashed, colors.outline)
            .borderRadius(WMCardCornerVar.value())
            .backgroundColor(colors.surfaceContainerLow)
            .color(colors.onSurfaceVariant)
            .padding(WMInlineGapVar.value())
            .gap(WMTightGapVar.value())
            .textAlign(TextAlign.Center)
    }
}

/** The dropzone while a file is dragged over it. */
val WMDropzoneActiveStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier.border(WMHairlineVar.value(), LineStyle.Solid, colors.primary).backgroundColor(colors.primaryContainer)
}

private const val FILE_INPUT_ID = "wm-upload-file-input"

/**
 * Penpot `Wagemix / Upload Dropzone`: drop a PDF or Word file, or browse. [onFile] receives
 * the chosen file; the caller validates it. The hidden file input is reachable from the
 * "browse files" label, so keyboards and screen readers get the native file picker.
 */
@Composable
fun WMUploadDropzone(
    strings: WMUploadDropzoneStrings,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    accept: String = ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    onFile: (File) -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    var dragging by remember { mutableStateOf(false) }
    Column(
        modifier = WMDropzoneStyle.toModifier()
            .then(if (dragging) WMDropzoneActiveStyle.toModifier() else Modifier)
            .onDragOver { event -> event.preventDefault(); if (enabled) dragging = true }
            .onDragLeave { dragging = false }
            .onDrop { event ->
                event.preventDefault()
                dragging = false
                if (enabled) event.dataTransfer?.files?.get(0)?.let(onFile)
            }
            .then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center
    ) {
        WMIconTile(WMIconGlyph.CloudUpload)
        Row(modifier = Modifier.gap(WMTightGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            SpanText(strings.dropHere, WMBodyStrongTextStyle.toModifier().color(colors.onSurface))
            Label(forId = FILE_INPUT_ID, attrs = WMBodyStrongTextStyle.toModifier().color(colors.primary).cursor(Cursor.Pointer).toAttrs()) {
                Text(strings.browseFiles)
            }
        }
        SpanText(strings.hint, WMBodySmallTextStyle.toModifier())
        Input(
            type = InputType.File,
            attrs = Modifier.display(DisplayStyle.None).toAttrs {
                id(FILE_INPUT_ID)
                accept(accept)
                if (!enabled) attr("disabled", "")
                onChange { event ->
                    val input = event.target as HTMLInputElement
                    input.files?.get(0)?.let(onFile)
                    input.value = ""
                }
            }
        )
    }
}

data class WMUploadDropzoneStrings(
    val dropHere: String,
    val browseFiles: String,
    val hint: String
)

/** The state of one upload row. */
enum class WMUploadStatus { Processing, Ready, Failed, Rejected }

/** What an upload row shows; built by the view model from the upload document. */
interface WMUploadRowPresentation {
    val uploadId: String
    /** "preschool-program-director.pdf" or the pasted link's host and path. */
    val title: String
    /** "Uploaded Oct 18, 2026 · 214 KB" or "Pasted link · Oct 12, 2026" */
    val meta: String
    val status: WMUploadStatus
    val fromUrl: Boolean
    /** The mix this upload produced, when ready. */
    val mixId: String?
    /** For an upload that produced no mix: why, and what to do about it. */
    val detail: String?

    companion object
}

fun WMUploadRowPresentation.Companion.of(
    uploadId: String,
    title: String,
    meta: String,
    status: WMUploadStatus,
    fromUrl: Boolean,
    mixId: String?,
    detail: String?
): WMUploadRowPresentation = WMUploadRowPresentationData(uploadId, title, meta, status, fromUrl, mixId, detail)

private data class WMUploadRowPresentationData(
    override val uploadId: String,
    override val title: String,
    override val meta: String,
    override val status: WMUploadStatus,
    override val fromUrl: Boolean,
    override val mixId: String?,
    override val detail: String?
): WMUploadRowPresentation

val WMUploadRowStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .fillMaxWidth()
        .padding(topBottom = WMListRowPaddingBlockVar.value())
        .gap(WMInlineGapVar.value())
        .borderBottom(width = WMHairlineVar.value(), style = LineStyle.Solid, color = colors.outlineVariant)
}

/**
 * Penpot `Wagemix / Job Description Row`: a file or link icon, the title and meta (and, for an
 * upload that produced no mix, why and what to do), a status badge ("Analyzing…", "Mix ready",
 * "Failed", "Rejected") and an [actions] slot ([Open mix], View file, Retry).
 */
@Composable
fun WMUploadRow(
    row: WMUploadRowPresentation,
    strings: WMUploadRowStrings,
    modifier: Modifier = Modifier,
    actions: @Composable RowScope.() -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    Row(
        modifier = WMUploadRowStyle.toModifier().role("listitem").then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        WMIconTile(if (row.fromUrl) WMIconGlyph.Link else WMIconGlyph.FileText, accent = false)
        Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())) {
            SpanText(row.title, WMBodyStrongTextStyle.toModifier())
            SpanText(row.meta, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
            row.detail?.let { SpanText(it, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant)) }
        }
        when (row.status) {
            WMUploadStatus.Processing -> WMBadge(strings.processing, kind = WMBadgeKind.AI, icon = WMIconGlyph.Sparkles)
            WMUploadStatus.Ready -> WMBadge(strings.ready, kind = WMBadgeKind.Success, icon = WMIconGlyph.CircleCheck)
            WMUploadStatus.Failed -> WMBadge(strings.failed, kind = WMBadgeKind.Error, icon = WMIconGlyph.TriangleAlert)
            WMUploadStatus.Rejected -> WMBadge(strings.rejected, kind = WMBadgeKind.Error, icon = WMIconGlyph.ShieldCheck)
        }
        actions()
    }
}

data class WMUploadRowStrings(
    val processing: String,
    val ready: String,
    val failed: String,
    val rejected: String
)
